import React from "react";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { closeModal as closeModalAction } from "actions/modal";
import { createAlert as createAlertAction } from "actions/alerts";
import { AlertType } from "actions/alerts/types";
import { adaAPI } from "services/api";
import keyConverter from "services/key-converter";
import {
  saveClientPlatform as saveClientPlatformAction,
  deletePlatform as deletePlatformAction,
  updatePlatform as updatePlatformAction
} from "actions";
import SettingsScreen from "./SettingsScreen";
import IntroScreen from "./IntroScreen";
import "./style.scss";

const INTRO_SCREEN = 1;
const SETTINGS_SCREEN = 2;

const ModalScreenMap = {
  [INTRO_SCREEN]: IntroScreen,
  [SETTINGS_SCREEN]: SettingsScreen
};

const ZendeskAgentWorkspaceModal = ({
  updatePlatform,
  platform,
  platformType,
  deletePlatform,
  closeModal,
  createAlert
}) => {
  const initialScreen = platform.get("isPublished") ? SETTINGS_SCREEN : INTRO_SCREEN;
  const [modalScreen, setModalScreen] = React.useState(initialScreen);
  const [errorInfo, setErrorInfo] = React.useState(null);
  const modalRef = React.useRef();

  const isSaveDisabled = (
    (platform.get("isPublished") && !platform.get("isDirty")) ||
    !platform.get("isValid") || platform.get("isLoading")
  );

  const updateZendeskAgentWorkspacePlatform = (payload) => {
    updatePlatform(platformType, payload);
  };

  const onSaveSuccess = (savedPlatform) => {
    setErrorInfo(null);
    updatePlatform(platformType, savedPlatform.get("record"), {
      isPublished: true,
      isLoading: false
    });
    createAlert({ message: "Successfully saved platform.", alertType: AlertType.SUCCESS });
    closeModal();
  };

  const onSaveError = (error) => {
    updatePlatform(platformType, {}, {
      isLoading: false
    });
    let apiError;

    try {
      apiError = JSON.parse(error.response.data.message);
    } catch (e) {
      apiError = { type: "generic", message: error.response.data.message };
    }

    setErrorInfo(apiError);
  };

  const saveClientPlatform = async (platformToSave) => {
    const isPublished = platformToSave.get("isPublished");
    updatePlatform(platformType, {}, {
      isLoading: true
    });

    try {
      await adaAPI.request({
        url: isPublished ? `/platforms/${platformType}/` : "/platforms/",
        method: isPublished ? "patch" : "post",
        data: keyConverter(platformToSave.get("record").toJS(), "underscore")
      });
      onSaveSuccess(platformToSave);
    } catch (error) {
      onSaveError(error);
    }
  };

  const CurrentModalScreen = ModalScreenMap[modalScreen];
  const ModalScreenProps = {
    [INTRO_SCREEN]: {
      gotoNextScreen: () => setModalScreen(SETTINGS_SCREEN),
      closeModal
    },
    [SETTINGS_SCREEN]: {
      platform,
      deletePlatform,
      updatePlatform,
      saveClientPlatform,
      updateZendeskAgentWorkspacePlatform,
      isSaveDisabled,
      closeModal,
      modalRef,
      errorInfo
    }
  };

  return (
    <div className="SettingsPlatformsZendeskAgentWorkspaceModal Modal__modal" ref={modalRef}>
      <h5 className="Modal__title">
        Configure Zendesk Social Messaging
      </h5>
      <CurrentModalScreen
        {...ModalScreenProps[modalScreen]}
      />
    </div>
  );
};

ZendeskAgentWorkspaceModal.propTypes = {
  updatePlatform: PropTypes.func.isRequired,
  platform: ImmutablePropTypes.map.isRequired,
  platformType: PropTypes.string.isRequired,
  saveClientPlatform: PropTypes.func.isRequired,
  deletePlatform: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  createAlert: PropTypes.func.isRequired
};

/**
 * map state to props
 * @param {Object} state
 * @param {Object} ownProps
 */
function mapState(state, ownProps) {
  const { platforms } = state;

  return {
    platforms,
    platform: platforms.get(ownProps.platformType)
  };
}

/**
 * Redux map dispatch to props
 * @param {Function} dispatch
 */
function mapDispatch(dispatch) {
  return bindActionCreators({
    saveClientPlatform: saveClientPlatformAction,
    deletePlatform: deletePlatformAction,
    updatePlatform: updatePlatformAction,
    closeModal: closeModalAction,
    createAlert: createAlertAction
  }, dispatch);
}

const Connector = connect(mapState, mapDispatch)(ZendeskAgentWorkspaceModal);

export default Connector;
